import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Check, X, Plus, Search, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { listFirefighters } from '@/api/firefighters';
import type { WildfireValues } from './WildfireSchema';
import { cn } from '@/lib/utils';

export interface FirefighterRecord {
  id: number;
  name: string;
  surname: string;
  note?: string | null;
}

interface ListResponse {
  firefighters: FirefighterRecord[];
  total: number;
  total_pages: number;
}

function initials(name: string, surname?: string): string {
  return `${(name?.[0] || '').toUpperCase()}${(surname?.[0] || '').toUpperCase()}` || '?';
}

interface FirefighterPickerProps {
  /**
   * Scope the options to one FPA. Without it a super admin sees every
   * tenant's firefighters, because resolve_tenant_filter passes a super
   * admin's request through verbatim — including "no tenant" meaning
   * "all tenants". Everyone else is force-scoped server-side already.
   */
  tenantId?: number | null;
  /**
   * Records whose names are already known — the wildfire being edited
   * embeds its firefighters complete with names. Without this the picker
   * can only name whoever happens to be in the default first page of
   * results, and everyone else renders as a bare `#id`.
   */
  seedKnown?: FirefighterRecord[];
}

/**
 * Multi-select of known Firefighter records plus freeform guest names.
 * Writes to the form's `firefighterIds` and `guestFirefighters` arrays.
 * Also auto-syncs `peopleInvolved` to the total count so manHours math
 * keeps working.
 */
export function FirefighterPicker({ tenantId, seedKnown }: FirefighterPickerProps = {}) {
  const form = useFormContext<WildfireValues>();
  const firefighterIds = (form.watch('firefighterIds') ?? []) as number[];
  const guestFirefighters = (form.watch('guestFirefighters') ?? []) as string[];

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FirefighterRecord[]>([]);
  const [knownById, setKnownById] = useState<Map<number, FirefighterRecord>>(
    () => new Map((seedKnown ?? []).map((f) => [f.id, f])),
  );
  const [loading, setLoading] = useState(false);
  const [guestDraft, setGuestDraft] = useState('');

  useEffect(() => {
    if (!seedKnown || seedKnown.length === 0) return;
    setKnownById((prev) => {
      const next = new Map(prev);
      for (const f of seedKnown) if (!next.has(f.id)) next.set(f.id, f);
      return next;
    });
  }, [seedKnown]);

  // Initial load + on query change (debounced).
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = { page: 1, page_size: 25 };
        if (query.trim()) params.search = query.trim();
        if (tenantId) params.tenant_id = tenantId;
        const data = (await listFirefighters(params)) as unknown as ListResponse;
        if (cancelled) return;
        setResults(data.firefighters || []);
        // Keep a lookup so we can render selected chips by id even when the
        // search query has scrolled them out of the visible results.
        setKnownById((prev) => {
          const next = new Map(prev);
          for (const f of data.firefighters || []) next.set(f.id, f);
          return next;
        });
      } catch (err) {
        console.warn('Failed to list firefighters for picker:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, tenantId]);

  // Keep `peopleInvolved` synced to (known + guests) so manHours stays right.
  //
  // Skipped on the first run: in edit mode form.reset() seeds firefighterIds
  // and peopleInvolved together, so an unguarded effect fired on the 0 -> N
  // transition and overwrote the fire's stored people_involved before the
  // user had touched anything — manHours was then recomputed from the wrong
  // number and both were silently rewritten on save.
  const totalCount = firefighterIds.length + guestFirefighters.length;
  const syncedOnceRef = useRef(false);
  useEffect(() => {
    if (!syncedOnceRef.current) {
      syncedOnceRef.current = true;
      return;
    }
    if (totalCount > 0) {
      form.setValue('peopleInvolved', totalCount, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const isSelected = (id: number) => firefighterIds.includes(id);
  const toggleKnown = (id: number) => {
    const next = isSelected(id)
      ? firefighterIds.filter((x) => x !== id)
      : [...firefighterIds, id];
    form.setValue('firefighterIds', next, { shouldDirty: true });
  };
  const removeKnown = (id: number) => {
    form.setValue(
      'firefighterIds',
      firefighterIds.filter((x) => x !== id),
      { shouldDirty: true },
    );
  };
  const addGuest = () => {
    const name = guestDraft.trim();
    if (!name) return;
    if (guestFirefighters.includes(name)) {
      setGuestDraft('');
      return;
    }
    form.setValue('guestFirefighters', [...guestFirefighters, name], { shouldDirty: true });
    setGuestDraft('');
  };
  const removeGuest = (name: string) => {
    form.setValue(
      'guestFirefighters',
      guestFirefighters.filter((g) => g !== name),
      { shouldDirty: true },
    );
  };

  const selectedKnown = useMemo(
    () =>
      firefighterIds
        .map((id) => knownById.get(id) ?? null)
        .map((f, idx) => f || ({ id: firefighterIds[idx]!, name: '', surname: '' } as FirefighterRecord)),
    [firefighterIds, knownById],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="gap-1">
              <Search className="h-4 w-4" />
              Search firefighters
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <div className="border-b border-border p-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name…"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading && results.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                  Loading…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                  No firefighters match.
                </div>
              ) : (
                <ul>
                  {results.map((f) => {
                    const active = isSelected(f.id);
                    return (
                      <li key={f.id}>
                        <button
                          type="button"
                          onClick={() => toggleKnown(f.id)}
                          className={cn(
                            'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                            active && 'bg-primary/5',
                          )}
                        >
                          <Avatar className="h-7 w-7">
                            <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-medium">
                              {initials(f.name, f.surname)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="flex-1 truncate">
                            {f.name} {f.surname}
                          </span>
                          {active && <Check className="h-4 w-4 text-primary" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <div className="flex items-center gap-1">
          <Input
            placeholder="Add guest name"
            value={guestDraft}
            onChange={(e) => setGuestDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addGuest();
              }
            }}
            className="h-9 w-44"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={addGuest}
            disabled={!guestDraft.trim()}
            title="Add guest"
          >
            <UserPlus className="h-4 w-4" />
          </Button>
        </div>

        <Badge variant="secondary" className="ml-auto">
          {totalCount} on scene
        </Badge>
      </div>

      {(selectedKnown.length > 0 || guestFirefighters.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {selectedKnown.map((f) => (
            <Badge key={`k-${f.id}`} variant="default" className="gap-1.5 pl-2 pr-1">
              {f.name ? `${f.name} ${f.surname || ''}`.trim() : `#${f.id}`}
              <button
                type="button"
                onClick={() => removeKnown(f.id)}
                className="rounded-full p-0.5 hover:bg-primary-foreground/20"
                aria-label="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {guestFirefighters.map((name) => (
            <Badge key={`g-${name}`} variant="outline" className="gap-1.5 pl-2 pr-1">
              <Plus className="h-3 w-3" /> {name}
              <button
                type="button"
                onClick={() => removeGuest(name)}
                className="rounded-full p-0.5 hover:bg-muted"
                aria-label="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default FirefighterPicker;
